import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { enUS, frFR } from "@material-ui/core/locale";
import { useIntl, defineMessages } from "react-intl";
import { DataGrid, GridOverlay } from "@material-ui/data-grid";
import { createTheme, MuiThemeProvider } from "@material-ui/core";
import AppTheme from "../../../theme";
import merge from "lodash/merge";
import { filterRows } from "./dataListHelper";
import { Header } from "./header";
import { filterTasklist } from "../../../redux/tasklist/tasklistActions";
import "./dataList.css";

/*
    Table to represent the list of tasks
    props:
        - tasks: array, list of tasks
        - fields: array, list of fields to display in the table
        - paneRef: ref, for the second pane in the split-pane, which handles scrolling
*/

// Theme overriding for stylizing table
const dataGridOverrides = {
    overrides: {
        MuiDataGrid: {
            root: {
                fontFamily: "Arial",
                fontSize: "12px",
                fontWeight: "400",
                userSelect: "none",
            },
        },
        MuiTableCell: {
            root: {
                fontFamily: "Arial",
                fontSize: "12px",
                alignItems: "center",
                padding: "0",
            },
        },
    },
};

const messages = defineMessages({
    noData: {
        id: "data-grid.no-data",
        defaultMessage: "No data",
        description: "No data message",
    },
});
export const DataList = (props) => {
    const intl = useIntl();
    const { rows = [], fields = [], dragging, onSelectionChange, onRowMouseDown, filters, rowHeight = 32, rowPerPage = 25 } = props;
    const [filteredRows, setFilteredRows] = useState(rows);
    const [filteredRows4DataGrid, setFilteredRows4DataGrid] = useState(rows);
    const [pageSize, setPageSize] = useState(rowPerPage);
    const [sortModel, setSortModel] = useState([]);
    const gridContainerRef = useRef(null);

    // Listen for clicks anywhere in the table to detect which row the user is interacting with.
    // This works by looking up the DOM tree to find the row container, then matching it back
    // to our actual data. Mainly used so we know which task to drag when user starts dragging.
    useEffect(() => {
        if (!onRowMouseDown || !gridContainerRef.current) return;

        const handleMouseDown = (e) => {
            const rowElement = e.target.closest("[data-rowindex]");
            if (rowElement) {
                const rowIndex = parseInt(rowElement.getAttribute("data-rowindex"));
                const targetRow = filteredRows4DataGrid[rowIndex];
                if (targetRow) {
                    onRowMouseDown(targetRow);
                }
            }
        };

        const gridContainer = gridContainerRef.current;
        gridContainer.addEventListener("mousedown", handleMouseDown);

        return () => {
            gridContainer.removeEventListener("mousedown", handleMouseDown);
        };
    }, [onRowMouseDown, filteredRows4DataGrid]);

    // Filtering
    const handleFiltering = (filter) => {
        const sorting = [
            {
                field: filter.field.field,
                sort: filter.order,
            },
        ];
        setSortModel([
            {
                field: filter.field.field,
                sort: filter.order,
            },
        ]);

        //setFilters({...filters, [filter.field.field]: filter});

        // raise filtering event
        props.filterTasklist({ ...filters, [filter.field.field]: filter });
    };

    useEffect(() => {
        setFilteredRows4DataGrid(
            filterRows(rows, filters).map((item) => {
                return { ...item, additionalAction: { ...item?.workOrderDetail?.additionalAction } };
            }),
        );
        setFilteredRows(filterRows(rows, filters));
    }, [filters, rows]);

    // Handle page size change
    const handlePageSizeChange = (pageSizeParams) => {
        setPageSize(pageSizeParams.pageSize);
    };

    // Header cells for the columns/fields to display for each task, as well as
    // the filter dropdown
    var columns = fields.map((field, i) => {
        const defaultW = 130;
        const labelLength = field.label ? intl.formatMessage(field.label).length : 0;
        let width = field.width ? field.width : labelLength > 6 ? defaultW + (labelLength - 6) * 5 : defaultW;
        return {
            headerName: <Header field={field} rows={filteredRows} filter={filters[field.field]} dragging={dragging} onFilter={handleFiltering} />,
            width: width,
            sortable: false,
            ...field,
        };
    });

    var localization = props.lang === "en" ? enUS : frFR;

    const theme = createTheme(
        {
            ...AppTheme,
            overrides: merge(AppTheme.overrides || {}, dataGridOverrides.overrides || {}),
        },
        localization,
    );

    return (
        <div ref={gridContainerRef} style={{ height: "100%", width: "100%" }}>
            <MuiThemeProvider theme={theme}>
                <DataGrid
                    rows={filteredRows4DataGrid}
                    disableColumnMenu
                    disableColumnReorder
                    rowHeight={rowHeight}
                    headerHeight={52}
                    columns={columns}
                    pageSize={pageSize}
                    onPageSizeChange={handlePageSizeChange}
                    sortModel={sortModel}
                    onSelectionModelChange={(newSelection) => {
                        onSelectionChange({
                            rows: newSelection.map((id) => filteredRows4DataGrid.find((row) => row.id === id)).filter(Boolean),
                        });
                    }}
                    hideFooterRowCount
                    hideFooterSelectedRowCount
                    loading={props.fetching}
                    components={{
                        noRowsOverlay: () => (
                            <GridOverlay>
                                <div>{intl.formatMessage(messages.noData)}</div>
                            </GridOverlay>
                        ),
                    }}
                />
            </MuiThemeProvider>
        </div>
    );
};

const mapStateToProps = (state) => {
    return {
        lang: state.domain.locale.lang,
        fetching: state.tasks.fetching,
        filters: state.tasklist.filters,
    };
};

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            filterTasklist,
        },
        dispatch,
    );

export default connect(mapStateToProps, mapDispatchToProps)(DataList);
